<script setup lang="ts">
import { Comark } from '@comark/vue'

definePageMeta({ layout: 'text-content-page' })

const { locale, t } = useI18n({
  useScope: 'local'
})

useHead({
  title: t('title'),
  htmlAttrs: {
    lang: locale.value
  },
  meta: [
    {
      name: 'description',
      content: t('description')
    }
  ]
})

const md_de = `# Geparste Zugverspätungen

Die geparsten Zugverspätungen decken nahezu alle Zugbewegungen in Deutschland seit September 2021 ab. Ab September 2023 erfassen wir auch die Entwicklung der Verspätungsprognosen der DB. Dieser Datensatz wird von Bahn-Vorhersage verwendet, um Verspätungsvorhersagemodelle zu trainieren.

## Datenmodell

Unser Datenmodell unterscheidet sich von vielen anderen Verkehrsdatenmodellen, aber wer mit GTFS vertraut ist, dem werden viele Spaltennamen bekannt vorkommen. Die Daten sind in tägliche Parquet-Dateien nach \`service_date\` (dem Datum, an dem die Fahrt begann) gruppiert. Jedes Halteereignis ist ein separater Datenpunkt, sodass die meisten Haltestellen zwei Datenpunkte erzeugen: einen für die Ankunft und einen für die Abfahrt. Wenn nur ein Typ benötigt wird, muss nach der Spalte \`is_arrival\` gefiltert werden.

Die Daten haben eine zeitliche Auflösung / Genauigkeit von einer Minute. Einige Spalten enthalten Dauern in Sekunden, aber das bedeutet nicht, dass die ursprünglichen Messungen Sekunden enthalten. Soweit wir wissen, schneidet die DB Sekunden für Ankunfts- und Abfahrtszeiten ab (z.B. wird 10:20:56 zu 10:20:00).

Da die Daten seit September 2023 die Entwicklung der Verspätungsprognosen erfassen, enthält nicht jeder Datenpunkt die endgültige Verspätung. Die Spalte \`is_final\` zeigt an, ob dies die letzte von Bahn-Vorhersage empfangene Prognose ist. Spaltennamen können verwirrend sein: Zum Beispiel enthält \`time_real\` nicht unbedingt die tatsächliche Zeit. Die Spalte sollte als \`time_prognosed\` behandelt werden. Endgültige Werte sind es nur, wenn nach \`is_final==true\` gefiltert wurde.

Die folgende Tabelle gibt vollständige Details zu allen Spalten.

::caption-table{caption="Datenschema der tabellarischen Daten. Datentyp in Kursivschrift. Alle Datumsangaben sind in UTC und verwenden Mikrosekunden."}
| Name | Beschreibung |
|------|--------------|
| \`trip_id\` | _Int64_: Gehashte ID, eindeutig pro Fahrt |
| \`time_schedule\` | _Datetime_: Geplante Zeit |
| \`time_real\` | _Datetime_: Prognostizierte Zeit. Wenn \`is_final==true\`, ist dies die tatsächliche Zeit. |
| \`update_timestamp\` | _Datetime_: Crawling-Zeitstempel der Echtzeitinformationen, die in diesem Datenpunkt enthalten sind. |
| \`delay\` | _Int32_: Differenz in Sekunden zwischen \`time_real\` und \`time_schedule\`. |
| \`dwell_time_schedule\` | _Int32_: Geplante Haltezeit an der Haltestelle. Fehlt, wenn nur Ankunfts- oder Abfahrtszeit verfügbar ist. |
| \`dwell_time_real\` | _Int32_: Prognostizierte (oder tatsächliche) Haltezeit an der Haltestelle. |
| \`is_final\` | _Boolean_: Zeigt an, ob dies das endgültige Echtzeit-Update ist. |
| \`is_arrival\` | _Boolean_: Ob der Datenpunkt eine Ankunft ist. |
| \`is_cancelled\` | _Boolean_: Gibt an, ob dieser Halt entfällt. Da ein Halt meistens aus Ankunft und Abfahrt besteht, kann auch nur eine davon entfallen. |
| \`is_regional\` | _Boolean_: Ob die Kategorie als Regionalzug betrachtet wird. |
| \`category\` | _String_: Kategorie des Zuges (zum Beispiel: ICE, RE, RB, S). |
| \`number\` | _String_: Zugnummer |
| \`line\` | _String_: Linienkennung (zum Beispiel: '6' für S6, '200' für RE 200 oder 'RB61' für ag RB61). |
| \`operator\` | _String_: Operator code des Eisenbahnunternehmens, das den Zug betreibt |
| \`stop_id\` | _Int64_: ID der Haltestelle; verlinkt zum Haltestellendatensatz (siehe [Haltestellen](/open-data/stops)). |
| \`platform_id_schedule\` | _Int64_: Geplante Bahnsteig-/Haltestellen-ID; verlinkt zum Haltestellendatensatz (siehe [Haltestellen](/open-data/stops)). |
| \`platform_id_real\` | _Int64_: Vorhergesagte (oder tatsächliche) Bahnsteig-/Haltestellen-ID. Die Haltestelle kann nicht von der geplanten Haltestelle abweichen. Verlinkt zum Haltestellendatensatz (siehe [Haltestellen](/open-data/stops)). |
| \`lat\` | _Float64_: Breitengrad der Haltestelle. <small class="text-muted">Die Quellen geben das Referenzsystem nicht an; wir nehmen EPSG:4326 an.</small> |
| \`lon\` | _Float64_: Längengrad der Haltestelle |
| \`distance_traveled\` | _Int32_: Geplante Entfernung in Metern, die die Fahrt seit ihrem Ursprung zurückgelegt hat. Vom Parser angenommen. |
| \`initial_scheduled_departure\` | _Datetime_: Ursprünglich geplante Abfahrtszeit am Ursprung. |
| \`initial_stop_id\` | _Int64_: Haltestellen-ID des Ursprungs; verlinkt zum Haltestellendatensatz (siehe [Haltestellen](/open-data/stops)). |
| \`stop_sequence\` | _UInt16_: Sequenznummer der Haltestelle, beginnend mit 1 für den Ursprung. |
| \`trip_headsign\` | _Int64_: Haltestellen-ID der Zielhaltestelle; verlinkt zum Haltestellendatensatz (siehe [Haltestellen](/open-data/stops)). |
| \`is_betriebshalt\` | _Boolean_: Ob die Haltestelle vom Parser als Betriebshalt (eine Haltestelle, an der Fahrgäste nicht ein- oder aussteigen dürfen) betrachtet wird. |
| \`pickup_drop_off_type\` | _Enum_: Entweder \`REGULAR\` (=0) oder \`NOT_ALLOWED\` (=1). |
| \`message_codes\` | _List(Int16)_: Liste der dem Ereignis zugeordneten Nachrichtencodes. |
::

## Datenvollständigkeit

Der Datensatz ist aufgrund von Crawling-Fehlern unvollständig. Fehlende Daten werden im folgenden Data-Paper im Absatz "Completeness and Scope" beschrieben. Das Paper beschreibt zudem auch den Prozess der Datensammlung genau.

::btn-link{href="https://opus.bibliothek.uni-augsburg.de/opus4/127213"}
<i class="icon icon-box-arrow-up-right"></i> Preprint Data Paper
::

## Daten

Die Daten sind in der Mobilithek verfügbar: <small class="text-muted">Du musst eingeloggt sein, um Daten zu sehen und anzufordern.</small>

::btn-link{href="https://mobilithek.info/offers/938616012299546624"}
<i class="icon icon-box-arrow-up-right"></i> Geparste Zugverspätungsdaten anfordern
::

Der Datensatz enthält jährliche \`.tar\` Archive mit täglichen Parquet-Dateien. Neue Archive werden hochgeladen, nachdem das Jahr abgeschlossen ist.

## Lizenz

Die Daten sind unter der [Open Database License](https://opendatacommons.org/licenses/odbl/){target="_blank"} verfügbar. Credits müssen an Bahn-Vorhersage, Deutsche Bahn, Trainline und DELFI zugeordnet werden.

_Wenn Du ein Problem mit dieser Dokumentation findest, dann öffne doch bitte ein Issue auf [GitLab](https://gitlab.com/bahnvorhersage/bahnvorhersage_frontend){target="_blank"}._
`

const md_en = `# Parsed Train Delays

The parsed train delays cover almost all train movements in Germany since September 2021. From September 2023 onwards we also capture the evolution of DB's delay prognoses. This dataset is used by Bahn-Vorhersage to train delay-prediction models.

## Data Model

Our data model differs from many other transport data models, but if you are familiar with GTFS many column names will be familiar. Data is grouped into daily Parquet files by \`service_date\` (the date when the trip started). Each stop event is a separate data point, so most stops produce two records: one for arrival and one for departure. If you only need one type, filter by the \`is_arrival\` column.

The data has a temporal resolution of one minute. Some columns contain durations in seconds, but this does not mean the original measurements include seconds. As far as we know, DB truncates seconds for arrival and departure times (e.g. 10:20:56 becomes 10:20:00).

Because the data captures the evolution of delay prognoses, not every data point contains the final delay. The column \`is_final\` indicates whether this is the last prognosis received by Bahn-Vorhersage. Column names can be confusing: for example \`time_real\` does not necessarily contain the actual time. Treat it as a prognosed time and only consider it the real time when \`is_final==true\`.

The following table gives full details for all columns.

::caption-table{caption="Data schema of the tabular data. Data type in italics. All datetimes are in UTC and use microseconds."}
| Name | Description |
|------|-------------|
| \`trip_id\` | _Int64_: Hashed ID unique to the trip |
| \`time_schedule\` | _Datetime_: Scheduled time |
| \`time_real\` | _Datetime_: Prognosed time. If \`is_final==true\`, this is the real time. |
| \`update_timestamp\` | _Datetime_: Crawling timestamp of the real-time information included in this data point. |
| \`delay\` | _Int32_: Difference in seconds between \`time_real\` and \`time_schedule\`. |
| \`dwell_time_schedule\` | _Int32_: Scheduled dwell time at the stop. Missing if only arrival or departure time is available. |
| \`dwell_time_real\` | _Int32_: Prognosed (or actual) dwell time at the stop. |
| \`is_final\` | _Boolean_: Indicates whether this is the final real-time update. |
| \`is_arrival\` | _Boolean_: Whether the datapoint is an arrival. |
| \`is_cancelled\` | _Boolean_: Whether the stop was cancelled. If the previous or next stop is cancelled, the arrival or departure might be cancelled even if the stop still occurred. |
| \`is_regional\` | _Boolean_: Whether the category is considered a regional train. |
| \`category\` | _String_: Category of the train (for example: ICE, RE, RB, S). |
| \`number\` | _String_: Train number |
| \`line\` | _String_: Line identifier (for example: '6' for S6, '200' for RE 200 or 'RB61' for RB61). |
| \`operator\` | _String_: Operator code of the railway company operating the train |
| \`stop_id\` | _Int64_: ID of the stop; links to the GTFS-like stop dataset (see [Stops](/open-data/stops)). |
| \`platform_id_schedule\` | _Int64_: Scheduled platform/stop ID; links to the GTFS-like stop dataset (see [Stops](/open-data/stops)). |
| \`platform_id_real\` | _Int64_: Predicted (or actual) platform/stop ID. The stop cannot differ from the scheduled stop. Links to the GTFS-like stop dataset (see [Stops](/open-data/stops)). |
| \`lat\` | _Float64_: Latitude of the stop. <small class="text-muted"> The sources do not state the reference system; we assume EPSG:4326.</small> |
| \`lon\` | _Float64_: Longitude of the stop |
| \`distance_traveled\` | _Int32_: Scheduled distance in meters traveled by the trip since its origin. Assumed by the parser. |
| \`initial_scheduled_departure\` | _Datetime_: Initial scheduled departure time at the origin. |
| \`initial_stop_id\` | _Int64_: Stop ID of the origin; links to the GTFS-like stop dataset (see [Stops](/open-data/stops)). |
| \`stop_sequence\` | _UInt16_: Sequence number of the stop, starting with 1 for the origin. |
| \`trip_headsign\` | _Int64_: Stop ID of the destination stop; links to the GTFS-like stop dataset (see [Stops](/open-data/stops)). |
| \`is_betriebshalt\` | _Boolean_: Whether the stop is considered a Betriebshalt (a stop where passengers are not allowed to board or alight) by the parser. |
| \`pickup_drop_off_type\` | _Enum_: Either \`REGULAR\` (=0) or \`NOT_ALLOWED\` (=1). |
| \`message_codes\` | _List(Int16)_: List of message codes associated with the event. |
::

## Data Completeness

The dataset is incomplete due to crawling failures. To avoid working with known wrong or missing data, consult the completeness analysis in the paper describing the data set.

::btn-link{href="https://opus.bibliothek.uni-augsburg.de/opus4/127213"}
<i class="icon icon-box-arrow-up-right"></i> Preprint Data Paper
::

## Data

The data is available in the Mobilithek: <small class="text-muted">You have to be logged in to see and request data.</small>

::btn-link{href="https://mobilithek.info/offers/938616012299546624"}
<i class="icon icon-box-arrow-up-right"></i> Request parsed train delay data
::

The dataset contains yearly \`.tar\` archives containing daily parquet files. New archives are uploaded after the year is complete.

## License

The data is available under the [Open Database License](https://opendatacommons.org/licenses/odbl/){target="_blank"}. Credits must be attributed to Bahn-Vorhersage, Deutsche Bahn, Trainline and DELFI.

_If you find any issue with this documentation, please consider contributing on [GitLab](https://gitlab.com/bahnvorhersage/bahnvorhersage_frontend){target="_blank"}._
`

const md = computed(() => (locale.value === 'de' ? md_de : md_en))
</script>

<template>
  <LocaleSwitcher />
  <Suspense>
    <Comark :options="{ autoUnwrap: true }">{{ md }}</Comark>
  </Suspense>
</template>

<i18n lang="yaml">
de:
  title: Geparste Verspätungen
  description: Geparste Zugverspätungsdaten aus Deutschland. Genau diese Daten werden verwendet, um unser Machine-Learning-System zu trainieren.
en:
  title: Parsed Delays
  description: Parsed train delay data from Germany. This exact data is used to train our machine learning system.
</i18n>
